import { forwardRef, useState, useEffect, useRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RichTextEditor } from "./RichTextEditor";
import { PdfPreview } from "./PdfPreview";
import { useIsMobile } from "../hooks/use-mobile";
import api, { formatApiErrorDetail } from "../lib/api";
import { updateCapture } from "../lib/offlineCaptures";
import { toDisplayHtml, deriveTitleFromHtml } from "../lib/richText";
import { Trash, PencilSimple, ChatCircleText, Sparkle, Lightbulb, Check, X, CircleNotch, FilePdf, LinkSimple, Tag as TagIcon, Star, Microphone, MicrophoneSlash, Smiley, ListChecks, ListBullets, CornersOut, DotsThreeVertical } from "@phosphor-icons/react";
import { downloadCapturesPdf } from "../lib/pdf";
import { Dialog, DialogContent } from "./ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

const VOICE_MAX_DURATION_MS = 30 * 60 * 1000;
// Rolling segments transcribed as soon as each one ends (instead of one
// shot at the very end) are what makes text appear progressively while
// still speaking. A segment now ends on a detected pause in speech rather
// than a fixed clock, per user request - this keeps perceived latency low
// (text appears right after you finish a sentence/phrase, not up to 20s
// later) while actually IMPROVING accuracy over a fixed timer (which can
// cut mid-word at an arbitrary boundary; a pause-triggered cut can't).
const VOICE_SILENCE_RMS_THRESHOLD = 0.02; // normalized 0-1 volume below which audio counts as "silence"
const VOICE_SILENCE_DURATION_MS = 800; // sustained silence this long = a real pause, not just a breath between words
const VOICE_MIN_SEGMENT_MS = 2000; // never rotate on a pause sooner than this into a segment, so Whisper gets something substantial
// Hard fallback cap for a continuous talker who never pauses long enough
// to trigger the above - without this one segment (and the wait for its
// text) could otherwise grow for the entire 30-minute VOICE_MAX_DURATION_MS.
const VOICE_SEGMENT_MS = 20 * 1000;
// A segment needs at least this much cumulative speech-level audio
// (summed across 100ms monitor ticks) before it's considered worth
// uploading - filters out a single brief blip/bump from being mistaken
// for real speech (see voiceSegmentHadSpeechRef below).
const VOICE_MIN_SPEECH_MS = 300;
// One global on/off switch (per user request) for the whole notes-editor
// toolbar row, not per-button - shared across every note via localStorage
// so collapsing it once keeps every note's editor compact from then on.
const TOOLBAR_COLLAPSED_KEY = "notesToolbarCollapsed";
// Must match Dashboard.jsx's NOTE_TOOLBAR_COLLAPSED_EVENT - broadcast when
// this key changes (from here or from Dashboard's Focus Mode toggle) so
// every already-mounted note pane (split-view can have 2 open at once)
// re-reads localStorage instead of only picking it up on next mount.
const TOOLBAR_COLLAPSED_EVENT = "notes-toolbar-collapsed-changed";
// Must match index.css's `.rich-editor-shell .ql-editor` min-height - image
// and PDF previews (non-zoom) are given this same fixed height so every
// note type occupies the same amount of vertical room, per user request.
const MEDIA_PREVIEW_HEIGHT = 491;

// Shared editing UI for a single capture (title/rich-text body/AI
// assist/annotate/delete + the fixed 2-second autosave loop), extracted out
// of the modal-only CapturePreviewDialog so the same logic can also be
// rendered inline inside a desktop split-view pane (NotesWorkspace). The
// modal wrapper (CaptureEditDialog) still uses this directly - nothing about
// the autosave/AI-assist behavior changed, only the container around it.
export const CaptureEditPanel = forwardRef(function CaptureEditPanel({ capture, onDelete, onSaved, onGenerateLink, onAddTag, tags, onToggleFavorite }, ref) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Every real caller only ever mounts this component once a valid capture
  // already exists (NotesWorkspace panes are keyed by capture id; the modal
  // wrapper early-returns null until capture is set), so it's safe - and
  // necessary - to seed state/refs straight from the prop right here rather
  // than solely through the effect below. RichTextEditor is an uncontrolled
  // component: its `defaultValue` is only ever read once, at mount. Relying
  // on the effect to populate initialHtmlRef (which only runs *after* this
  // first render has already been committed with whatever defaultValue was
  // read during it) meant every single note opened rendered with a blank
  // body - the title looked fine only because it's a controlled input that
  // gets a second, corrective render once the effect's setTitle() lands.
  const [title, setTitle] = useState(() => capture?.title || "");
  const [comment, setComment] = useState(() => capture?.comment || "");
  const [aiSuggestion, setAiSuggestion] = useState(() => capture?.ai_suggestion || null);
  const [organizePreview, setOrganizePreview] = useState(null);
  const [organizing, setOrganizing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [recording, setRecording] = useState(false);
  // Counts in-flight segment transcriptions rather than a single boolean -
  // segments upload concurrently (see voiceInsertQueueRef below), so a
  // boolean would get set back to false by whichever overlapping upload
  // happens to finish first even while another one is still pending.
  const [pendingTranscriptions, setPendingTranscriptions] = useState(0);
  const transcribing = pendingTranscriptions > 0;
  const [emojifying, setEmojifying] = useState(false);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(() => localStorage.getItem(TOOLBAR_COLLAPSED_KEY) === "1");
  const editorRef = useRef(null);
  const initialHtmlRef = useRef(toDisplayHtml(capture?.text_content_html || capture?.text_content));
  const titleRef = useRef(capture?.title || "");
  const commentRef = useRef(capture?.comment || "");
  const latestHtmlRef = useRef(initialHtmlRef.current);
  const savedRef = useRef({ title: titleRef.current, comment: commentRef.current, html: initialHtmlRef.current });
  const titleManuallyEditedRef = useRef((capture?.title || "").trim() !== "");
  // Tracks which note's data is currently loaded into the fields above -
  // used below to tell a genuine "switched to a different note" prop change
  // apart from an autosave echo (onSaved() re-delivering the SAME note with
  // a fresh object reference every ~2s while the user keeps typing).
  const loadedCaptureIdRef = useRef(capture?.id);

  useEffect(() => {
    // Only re-sync from props when this instance is now showing a genuinely
    // different note. Without this guard, every autosave's onSaved() echo
    // (same note, new object reference) re-ran this reset and clobbered
    // whatever the user had typed in the ~2s since that save was sent,
    // which is what caused the reported jitter/shake while editing.
    if (capture && capture.id !== loadedCaptureIdRef.current) {
      loadedCaptureIdRef.current = capture.id;
      const initialTitle = capture.title || "";
      const initialComment = capture.comment || "";
      const html = toDisplayHtml(capture.text_content_html || capture.text_content);
      setTitle(initialTitle);
      setComment(initialComment);
      titleRef.current = initialTitle;
      commentRef.current = initialComment;
      titleManuallyEditedRef.current = initialTitle.trim() !== "";
      setAiSuggestion(capture.ai_suggestion || null);
      setOrganizePreview(null);
      initialHtmlRef.current = html;
      latestHtmlRef.current = html;
      savedRef.current = { title: initialTitle, comment: initialComment, html };
      setSaveStatus("idle");
    }
  }, [capture]);

  useEffect(() => {
    const syncToolbarCollapsed = () => setToolbarCollapsed(localStorage.getItem(TOOLBAR_COLLAPSED_KEY) === "1");
    window.addEventListener(TOOLBAR_COLLAPSED_EVENT, syncToolbarCollapsed);
    return () => window.removeEventListener(TOOLBAR_COLLAPSED_EVENT, syncToolbarCollapsed);
  }, []);

  const [fullImageSrc, setFullImageSrc] = useState(null);
  const [fullPdfSrc, setFullPdfSrc] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  useEffect(() => {
    if (!capture || capture.type === "text") return;
    setFullImageSrc(null);
    setFullPdfSrc(null);
    let cancelled = false;
    api.get(`/captures/${capture.id}`).then(({ data }) => {
      if (cancelled) return;
      setFullImageSrc(data.image_data);
      setFullPdfSrc(data.pdf_data);
    }).catch(() => {
      // Non-critical - the thumbnail already passed in is still viewable.
    });
    return () => {
      cancelled = true;
    };
  }, [capture]);

  const performSaveRef = useRef(() => {});
  useEffect(() => {
    const saveIfHidden = () => {
      if (document.visibilityState === "hidden") performSaveRef.current();
    };
    document.addEventListener("visibilitychange", saveIfHidden);
    window.addEventListener("pagehide", saveIfHidden);
    return () => {
      document.removeEventListener("visibilitychange", saveIfHidden);
      window.removeEventListener("pagehide", saveIfHidden);
    };
  }, []);

  const periodicSaveRef = useRef(() => {});
  useEffect(() => {
    if (!capture) return;
    const interval = setInterval(() => periodicSaveRef.current(), 2000);
    return () => clearInterval(interval);
  }, [capture]);

  const savingPromiseRef = useRef(null);
  const voiceStreamRef = useRef(null);
  const voiceRecorderRef = useRef(null);
  const voiceActiveRef = useRef(false);
  const voiceMaxDurationTimerRef = useRef(null);
  const voiceSegmentTimerRef = useRef(null);
  const voiceAudioContextRef = useRef(null);
  const voiceSilenceCheckIntervalRef = useRef(null);
  const voiceSilenceStartRef = useRef(null);
  const voiceSegmentStartTimeRef = useRef(0);
  // True once at least VOICE_MIN_SPEECH_MS of cumulative RMS-over-threshold
  // audio has been seen in the CURRENTLY active segment - read at the top
  // of onstop (before it's reset by the next segment starting) to decide
  // whether that segment is worth uploading at all. Requiring a little
  // cumulative speech (not just one 100ms frame) avoids uploading a brief
  // bump/click as if it were real speech, which Whisper can hallucinate
  // actual sentences out of. Without this gate at all, a pause-triggered
  // cut on a segment that was silent from the start (e.g. the user just
  // stopped talking and the next segment begins in silence) would get
  // re-cut every ~2s for as long as the pause lasts, spamming Whisper with
  // empty clips.
  const voiceSegmentHadSpeechRef = useRef(false);
  const voiceSegmentSpeechMsRef = useRef(0);
  // Resolved by the final segment's onstop (the one that does NOT restart
  // a new segment) so stopRecording's caller can await the moment the
  // last segment's upload has actually been chained onto
  // voiceInsertQueueRef - see stopRecording/finalizeAndSave below.
  const voiceStopResolveRef = useRef(null);
  // Indirect through a ref (same pattern as performSaveRef above) so this
  // unmount-cleanup effect can be declared here, before the early return
  // below, while the actual stopRecording function (which needs `capture`
  // and is defined after that return) is only wired in further down.
  const stopRecordingRef = useRef(() => {});
  useEffect(() => () => stopRecordingRef.current(), []);
  // Uploads for different segments run concurrently (for speed), but their
  // resulting text is inserted strictly in segment order via this queue -
  // otherwise a slow 20s segment finishing after a fast short final
  // segment would insert "...segment2 segment1..." instead of in order.
  const voiceInsertQueueRef = useRef(Promise.resolve());

  // Exposes an imperative finalizeAndSave() so a parent container (modal
  // close handler, or a split-view tab's close button) can force a final
  // save before tearing this pane down - indirect through performSaveRef so
  // it's safe to register before finalizeAndSave itself is defined below.
  useImperativeHandle(ref, () => ({
    finalizeAndSave: () => performSaveRef.current(),
  }), []);

  if (!capture) return null;
  const isText = capture.type === "text";
  const isPdf = capture.type === "pdf";

  const performSave = async () => {
    if (savingPromiseRef.current) await savingPromiseRef.current;
    const currentTitle = titleRef.current;
    const currentComment = commentRef.current;
    const currentHtml = isText ? latestHtmlRef.current : null;
    const changed = currentTitle !== savedRef.current.title || currentComment !== savedRef.current.comment || (isText && currentHtml !== savedRef.current.html);
    if (!changed) return;
    setSaveStatus("saving");
    const promise = (async () => {
      try {
        const payload = { title: currentTitle, comment: currentComment };
        if (isText) payload.text_content_html = currentHtml;
        const data = await updateCapture(capture.id, payload);
        savedRef.current = { title: currentTitle, comment: currentComment, html: isText ? currentHtml : savedRef.current.html };
        onSaved(data);
        setSaveStatus("saved");
      } catch (e) {
        setSaveStatus("error");
        toast.error(t("editDialog.autosaveFailedError"));
      }
    })();
    savingPromiseRef.current = promise;
    await promise;
    if (savingPromiseRef.current === promise) savingPromiseRef.current = null;
  };
  periodicSaveRef.current = performSave;

  const finalizeAndSave = async () => {
    // If a recording is still in progress when this fires (e.g. the dialog
    // is closed without clicking stop), stop it first so its final partial
    // segment gets queued - then wait for every queued segment's transcript
    // to actually land in the editor before reading latestHtmlRef/saving,
    // otherwise closing shortly after stopping could save the capture
    // BEFORE the last segment's text was inserted, silently losing it.
    if (voiceActiveRef.current) await stopRecording();
    await voiceInsertQueueRef.current;
    if (isText && !titleManuallyEditedRef.current) {
      const finalHtml = latestHtmlRef.current;
      const derived = deriveTitleFromHtml(finalHtml);
      if (derived && derived !== titleRef.current) {
        setTitle(derived);
        titleRef.current = derived;
      }
    }
    await performSave();
  };
  performSaveRef.current = finalizeAndSave;

  const handleAnnotate = async () => {
    await performSave();
    navigate(`/capture/${capture.id}`);
  };

  // Voice input: rotates through a new ~20s MediaRecorder segment on the
  // same underlying mic stream for as long as the user keeps recording,
  // transcribing and inserting each segment as soon as it lands - so text
  // appears progressively while still speaking instead of only once at
  // the very end (per user request; a prior version with no segmentation
  // had better per-call accuracy but zero feedback until you stopped).
  const uploadAndTranscribeSegment = (blob) => {
    if (!blob || blob.size === 0) return Promise.resolve();
    setPendingTranscriptions((n) => n + 1);
    const formData = new FormData();
    formData.append("audio", blob, "recording.webm");
    // Hinting Whisper with the app's current language noticeably improves
    // accuracy over pure auto-detection, especially for short clips or
    // clips with background noise - auto-detect has to spend part of the
    // clip just guessing the language before it can transcribe well.
    formData.append("language", i18n.language?.startsWith("zh") ? "zh" : "en");
    const requestPromise = api
      .post(`/captures/${capture.id}/transcribe`, formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((res) => res.data)
      .catch(() => {
        toast.error(t("editDialog.voiceTranscribeFailedError"));
        return null;
      });
    voiceInsertQueueRef.current = voiceInsertQueueRef.current.then(async () => {
      const data = await requestPromise;
      if (data?.text) {
        // Avoid jamming this segment's text straight onto whatever came
        // right before it with no separator (e.g. a typed word followed
        // immediately by "...and this is...") - only the FIRST inserted
        // segment after opening the note can legitimately have nothing
        // preceding it.
        const existingHtml = editorRef.current?.getHtml?.() ?? latestHtmlRef.current ?? "";
        // Quill serializes a trailing space as the "&nbsp;" entity, so a
        // plain tag-strip's last character would be ";" (from "&nbsp;"),
        // not whitespace - decoding entities first avoids inserting a
        // redundant extra leading space after every segment.
        const plainTail = existingHtml.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
        const needsLeadingSpace = plainTail.length > 0 && !/\s$/.test(plainTail);
        editorRef.current?.insertTextAtCursor(`${needsLeadingSpace ? " " : ""}${data.text} `);
        // Quill's insertTextAtCursor moves the cursor via setSelection,
        // which - like any DOM Selection change inside a contenteditable -
        // implicitly focuses the editor root again even though startRecording
        // explicitly blurred it to hide the keyboard. On mobile this made the
        // keyboard pop back up after EVERY transcribed segment while still
        // recording (reported bug). Still actively recording on mobile -> blur
        // again right away so the keyboard never gets a chance to show.
        if (isMobile && voiceActiveRef.current) editorRef.current?.blurEditor();
      }
      setPendingTranscriptions((n) => n - 1);
    });
    return voiceInsertQueueRef.current;
  };

  // Starts (or, from its own onstop handler, seamlessly restarts) one
  // segment on the given stream - ended early by startSilenceMonitor below
  // on a detected pause, or by this hard VOICE_SEGMENT_MS fallback timer
  // for a continuous talker. Deliberately does NOT touch any shared ref
  // for its own audio chunks (keeps them in a closure) so overlapping
  // in-flight segments never clobber each other's audio data.
  const startVoiceSegment = (stream) => {
    voiceSegmentStartTimeRef.current = Date.now();
    voiceSilenceStartRef.current = null;
    voiceSegmentHadSpeechRef.current = false;
    voiceSegmentSpeechMsRef.current = 0;
    const recorder = new MediaRecorder(stream);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      // Read BEFORE deciding to restart below, since restarting resets
      // this same ref for the next segment.
      const hadSpeech = voiceSegmentHadSpeechRef.current;
      if (hadSpeech) {
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        uploadAndTranscribeSegment(blob);
      }
      // Still actively recording on THIS SAME stream (user hasn't clicked
      // stop/started a new session and the 30-minute cap hasn't fired) -
      // roll straight onto the next segment so there's no audible gap.
      // Checking stream identity (not just voiceActiveRef) matters: if the
      // user stops and immediately starts a new recording before this
      // onstop callback runs, voiceActiveRef is already true again for the
      // NEW session, but `stream` here is the OLD, already-released one -
      // without this check that would spawn an orphaned recorder on a dead
      // stream and stomp on the new session's refs.
      if (voiceActiveRef.current && voiceStreamRef.current === stream) {
        try {
          startVoiceSegment(stream);
        } catch (e) {
          // Dead/released stream - nothing more to do, the active session
          // (if any) is already running on its own separate stream.
        }
      } else {
        // This was the FINAL segment for this stream (user clicked stop,
        // or the 30-minute cap fired) - its upload (if any) has now been
        // chained onto voiceInsertQueueRef above, so it's safe to let
        // whoever is waiting on stopRecording()'s promise proceed.
        voiceStopResolveRef.current?.();
        voiceStopResolveRef.current = null;
      }
    };
    recorder.start();
    voiceRecorderRef.current = recorder;
    voiceSegmentTimerRef.current = setTimeout(() => {
      if (recorder.state !== "inactive") recorder.stop();
    }, VOICE_SEGMENT_MS);
  };

  // Polls the mic's live volume (Web Audio API) every 100ms for the whole
  // recording session (independent of segment rotation) and ends the
  // CURRENT segment the moment a real pause is detected, instead of
  // always waiting out the VOICE_SEGMENT_MS fallback - this is what makes
  // transcribed text show up right after a sentence/phrase instead of up
  // to 20s later. Just calls recorder.stop() on whatever segment is
  // currently active; startVoiceSegment's own onstop handler takes it from
  // there exactly like the fallback timer firing would.
  const startSilenceMonitor = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return; // unsupported browser - VOICE_SEGMENT_MS fallback still rotates segments
    const audioContext = new AudioContextClass();
    // On some browsers (notably iOS Safari, since this context is created
    // inside an async startRecording() after an await) a freshly-created
    // context can start "suspended" instead of "running" - the analyser
    // then only ever reads a flat/silent buffer, which looks exactly like
    // sustained silence and cuts every segment ~2s in even mid-speech.
    try { audioContext.resume(); } catch (e) {}
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    voiceAudioContextRef.current = audioContext;
    const buffer = new Uint8Array(analyser.frequencyBinCount);
    voiceSilenceCheckIntervalRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) {
        const normalized = (buffer[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / buffer.length);
      const now = Date.now();
      if (rms < VOICE_SILENCE_RMS_THRESHOLD) {
        if (voiceSilenceStartRef.current == null) voiceSilenceStartRef.current = now;
        const recorder = voiceRecorderRef.current;
        if (
          now - voiceSilenceStartRef.current >= VOICE_SILENCE_DURATION_MS &&
          now - voiceSegmentStartTimeRef.current >= VOICE_MIN_SEGMENT_MS &&
          recorder &&
          recorder.state !== "inactive"
        ) {
          recorder.stop();
        }
      } else {
        voiceSilenceStartRef.current = null; // still talking - reset the pause timer
        voiceSegmentSpeechMsRef.current += 100;
        if (voiceSegmentSpeechMsRef.current >= VOICE_MIN_SPEECH_MS) voiceSegmentHadSpeechRef.current = true;
      }
    }, 100);
  };

  const stopSilenceMonitor = () => {
    clearInterval(voiceSilenceCheckIntervalRef.current);
    voiceSilenceCheckIntervalRef.current = null;
    try { voiceAudioContextRef.current?.close(); } catch (e) {}
    voiceAudioContextRef.current = null;
    voiceSilenceStartRef.current = null;
  };

  const stopRecording = (reason) => {
    if (!voiceActiveRef.current) return Promise.resolve();
    voiceActiveRef.current = false;
    setRecording(false);
    clearTimeout(voiceMaxDurationTimerRef.current);
    clearTimeout(voiceSegmentTimerRef.current);
    stopSilenceMonitor();
    const recorder = voiceRecorderRef.current;
    // Resolves once the currently-active segment's onstop handler has run
    // and (if it had any speech) chained its upload onto
    // voiceInsertQueueRef - callers that need the final segment's text to
    // actually land before proceeding (finalizeAndSave) await this instead
    // of racing ahead the instant stop() is merely requested.
    const stopPromise = recorder && recorder.state !== "inactive"
      ? new Promise((resolve) => {
          voiceStopResolveRef.current = resolve;
          // Safety net: if recorder.stop() below throws (the iOS Safari
          // case the comment above describes) or the browser simply never
          // fires onstop, voiceStopResolveRef would otherwise never be
          // called and this promise - and whatever awaits it in
          // finalizeAndSave, like closing the note - would hang forever.
          setTimeout(resolve, 3000);
        })
      : Promise.resolve();
    try {
      // Some browsers (notably iOS Safari) throw here if stop() is called
      // on a recorder that hasn't emitted any data yet (e.g. stopped within
      // ~100ms of starting). Without this try/catch that throw skipped the
      // stream-release lines below entirely, leaving the mic's OS-level
      // "in use" indicator on and the underlying tracks never released -
      // which is what actually looked like "recording won't stop" even
      // though the button/UI state above had already flipped back.
      if (recorder && recorder.state !== "inactive") recorder.stop();
    } catch (e) {
      // Swallow - the mic release below still needs to run regardless.
    }
    voiceStreamRef.current?.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        // Same reasoning as above - never let one track's failure block the rest.
      }
    });
    voiceStreamRef.current = null;
    if (reason === "max-duration") toast.info(t("editDialog.voiceMaxDurationReached"));
    return stopPromise;
  };

  const startRecording = async () => {
    if (voiceActiveRef.current) return;
    // Set eagerly (before the async getUserMedia call resolves) so a second
    // rapid click on the button while the permission prompt/negotiation is
    // still pending can't slip through this guard and open a SECOND stream
    // - that second stream would overwrite voiceStreamRef/voiceRecorderRef,
    // leaking the first one (never released), which is another path to the
    // same "mic never actually turns off" symptom as above.
    voiceActiveRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceStreamRef.current = stream;
      setRecording(true);
      // Dismissing the keyboard (and, via toolbarCollapsed-style hiding
      // below, the formatting toolbar) frees up most of the screen on
      // mobile for the recording indicator while dictating - neither is
      // needed since typing isn't how text gets in during voice input.
      if (isMobile && isText) editorRef.current?.blurEditor();
      startVoiceSegment(stream);
      startSilenceMonitor(stream);
      voiceMaxDurationTimerRef.current = setTimeout(() => stopRecording("max-duration"), VOICE_MAX_DURATION_MS);
    } catch (e) {
      voiceActiveRef.current = false;
      toast.error(t("editDialog.voiceMicPermissionError"));
    }
  };
  stopRecordingRef.current = stopRecording;

  // "AI Emoji" button: takes whatever text is currently highlighted in the
  // editor, sends it to the emoji-convert endpoint, and replaces that exact
  // same range with the result (original words kept, matching emoji
  // appended after recognized keywords per the confirmed product choice).
  const handleEmojiConvert = async () => {
    const selection = editorRef.current?.getSelectedText();
    if (!selection) {
      toast.error(t("editDialog.emojiConvertNeedsSelectionError"));
      return;
    }
    setEmojifying(true);
    try {
      const { data } = await api.post("/captures/emoji-convert", { text: selection.text, language: i18n.language });
      editorRef.current?.replaceRange(selection.index, selection.length, data.result);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("editDialog.emojiConvertFailedError"));
    } finally {
      setEmojifying(false);
    }
  };

  const runAiAssist = async (mode) => {
    const currentHtml = latestHtmlRef.current;
    if (!currentHtml.replace(/<[^>]+>/g, "").trim()) {
      toast.error(t("editDialog.aiAssistNeedsContentError"));
      return;
    }
    const setLoading = mode === "organize" ? setOrganizing : setSuggesting;
    setLoading(true);
    try {
      if (currentHtml !== savedRef.current.html) {
        const { data } = await api.patch(`/captures/${capture.id}`, { text_content_html: currentHtml });
        onSaved(data);
        savedRef.current.html = currentHtml;
      }
      const { data } = await api.post(`/captures/${capture.id}/ai-assist`, { mode, language: i18n.language });
      if (mode === "organize") {
        setOrganizePreview(data.result);
      } else {
        setAiSuggestion(data.ai_suggestion);
        onSaved(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t(mode === "organize" ? "editDialog.aiOrganizeFailedError" : "editDialog.aiSuggestFailedError"));
    } finally {
      setLoading(false);
    }
  };

  const applyOrganizePreview = async () => {
    editorRef.current?.setHtml(organizePreview);
    latestHtmlRef.current = organizePreview;
    setOrganizePreview(null);
    try {
      const { data } = await api.patch(`/captures/${capture.id}`, { text_content_html: organizePreview });
      onSaved(data);
      savedRef.current.html = data.text_content_html || "";
    } catch (e) {
      // Content is still applied in the editor locally; the next 2-second
      // autosave tick will retry persisting it shortly.
    }
  };

  const dismissAiSuggestion = async () => {
    setAiSuggestion(null);
    try {
      const { data } = await api.delete(`/captures/${capture.id}/ai-suggestion`);
      onSaved(data);
    } catch (e) {
      // Non-critical - the panel is already hidden locally.
    }
  };

  // Mirrors the quick-actions available on a note card in the main grid
  // (favorite / PDF export / share link / add tag). Each action only
  // renders once its corresponding prop is actually wired up by the
  // caller - NotesWorkspace passes all three, CaptureEditDialog (the modal
  // used by Dashboard grid view, mobile, Tasks/Schedule favorite-note
  // shortcuts, and TaskDetailDialog's nested linked-note editor) only
  // wires up onAddTag (self-contained tagging, see CapturePreviewDialog.jsx)
  // so only "添加标签" shows there, not favorite/link. Kept on the same row
  // as AI Organize/Suggest to save space.
  const favoritesTag = tags?.find((tg) => tg.is_favorites);
  const isFavorite = favoritesTag ? capture.tags?.includes(favoritesTag.id) : false;
  const hasQuickActions = !!(onGenerateLink || onAddTag || onToggleFavorite);
  // Only favorite stays as its own always-visible icon (most-used action);
  // PDF/link/tag move into a "more" dropdown per user request (2026-09-11)
  // to keep the merged top row from growing an icon every time a new
  // action gets added.
  const renderQuickActions = () => (
    <div className="flex items-center gap-1">
      {onToggleFavorite && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          title={isFavorite ? t("captureCard.unfavorite") : t("captureCard.favorite")}
          data-testid={`note-pane-favorite-${capture.id}`}
          onClick={() => onToggleFavorite(capture)}
          className={`toolbar-icon-btn rounded-none h-7 w-7 p-0 ${isFavorite ? "text-amber-500" : ""}`}
        >
          <Star size={13} weight={isFavorite ? "fill" : "regular"} />
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            title={t("captureCard.moreActions")}
            data-testid={`note-pane-more-${capture.id}`}
            className="toolbar-icon-btn rounded-none h-7 w-7 p-0"
          >
            <DotsThreeVertical size={15} weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-none">
          <DropdownMenuItem data-testid={`note-pane-pdf-${capture.id}`} onClick={() => downloadCapturesPdf([capture.id])} className="gap-2">
            <FilePdf size={14} /> {t("captureCard.downloadPdf")}
          </DropdownMenuItem>
          {onGenerateLink && (
            <DropdownMenuItem data-testid={`note-pane-link-${capture.id}`} onClick={() => onGenerateLink([capture.id])} className="gap-2">
              <LinkSimple size={14} /> {t("captureCard.generateLink")}
            </DropdownMenuItem>
          )}
          {onAddTag && (
            <DropdownMenuItem data-testid={`note-pane-tag-${capture.id}`} onClick={() => onAddTag(capture)} className="gap-2">
              <TagIcon size={14} /> {t("captureCard.addTag")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`capture-edit-panel-${capture.id}`}>
      <div className="flex items-center gap-2">
        <Label htmlFor={`edit-title-${capture.id}`} className="text-xs shrink-0 text-muted-foreground">{t("editDialog.titleLabel")}</Label>
        <Input
          id={`edit-title-${capture.id}`}
          data-testid={`capture-edit-title-input-${capture.id}`}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            titleRef.current = e.target.value;
            titleManuallyEditedRef.current = e.target.value.trim() !== "";
          }}
          className="rounded-none h-8 text-sm flex-1"
        />
      </div>
      {isText ? (
        <div className="space-y-2">
          <div className="rich-editor-shell compact-note-toolbar">
            <div id={`quill-toolbar-${capture.id}`} className="ql-toolbar ql-snow" style={(toolbarCollapsed || (isMobile && recording)) ? { display: "none" } : undefined}>
              <span className="ql-formats">
                <select className="ql-size" defaultValue="false">
                  <option value="small" />
                  <option value="false" />
                  <option value="large" />
                  <option value="huge" />
                </select>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-bold font-bold">B</button>
                <button type="button" className="ql-italic italic">I</button>
                <button type="button" className="ql-underline underline">U</button>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-list" value="check" title={t("editDialog.checklistLabel")}>
                  <ListChecks size={11} />
                </button>
                <button type="button" className="ql-list" value="bullet" title={t("editDialog.bulletListLabel")}>
                  <ListBullets size={11} />
                </button>
              </span>
              {isText && (
                <span className="ql-formats">
                  {!isMobile && (
                    <Button
                      type="button"
                      data-testid={`capture-edit-voice-button-${capture.id}`}
                      variant={recording ? "destructive" : "outline"}
                      size="sm"
                      title={recording ? t("editDialog.voiceRecording") : t("editDialog.voiceInput")}
                      className="toolbar-icon-btn rounded-none h-7 w-7 p-0"
                      // Buttons steal focus on click by default, which blurs the
                      // Quill editor and makes the blinking text cursor visually
                      // disappear mid-recording. Preventing the mousedown default
                      // keeps focus (and the caret) exactly where it was.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={recording ? () => stopRecording() : startRecording}
                    >
                      {recording ? <MicrophoneSlash size={13} weight="bold" className="animate-pulse" /> : <Microphone size={13} />}
                    </Button>
                  )}
                  <Button
                    type="button"
                    data-testid={`capture-edit-emoji-button-${capture.id}`}
                    variant="outline"
                    size="sm"
                    title={t("editDialog.emojiConvert")}
                    className="toolbar-icon-btn rounded-none h-7 w-7 p-0"
                    disabled={emojifying}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleEmojiConvert}
                  >
                    {emojifying ? <CircleNotch size={13} className="animate-spin" /> : <Smiley size={13} />}
                  </Button>
                  {transcribing && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground px-1" data-testid={`voice-transcribing-indicator-${capture.id}`}>
                      <CircleNotch size={12} className="animate-spin" /> {t("editDialog.voiceTranscribing")}
                    </span>
                  )}
                </span>
              )}
              <span className="ql-formats">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={organizing || suggesting}
                  onClick={() => runAiAssist("organize")}
                  data-testid={`ai-organize-button-${capture.id}`}
                  title={organizing ? t("editDialog.aiOrganizing") : t("editDialog.aiOrganize")}
                  className="toolbar-icon-btn rounded-none h-7 w-7 p-0"
                >
                  {organizing ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="bold" />}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={organizing || suggesting}
                  onClick={() => runAiAssist("suggest")}
                  data-testid={`ai-suggest-button-${capture.id}`}
                  title={suggesting ? t("editDialog.aiSuggesting") : t("editDialog.aiSuggest")}
                  className="toolbar-icon-btn rounded-none h-7 w-7 p-0"
                >
                  {suggesting ? <CircleNotch size={13} className="animate-spin" /> : <Lightbulb size={13} weight="bold" />}
                </Button>
              </span>
              <span className="ql-toolbar-spacer" />
              {hasQuickActions && <span className="ql-formats">{renderQuickActions()}</span>}
              <span className="ql-formats">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  title={t("editDialog.deleteRecord")}
                  data-testid={`capture-edit-delete-button-${capture.id}`}
                  onClick={() => onDelete(capture.id)}
                  className="toolbar-delete-btn rounded-none"
                >
                  <Trash size={13} />
                </Button>
              </span>
            </div>
            <div className="relative">
              <RichTextEditor
                key={capture.id}
                ref={editorRef}
                defaultValue={initialHtmlRef.current}
                toolbarContainer={`#quill-toolbar-${capture.id}`}
                dataTestId={`capture-edit-text-content-editor-${capture.id}`}
                onChange={(html) => {
                  latestHtmlRef.current = html;
                }}
              />
              {recording && (
                <div
                  className="pointer-events-none absolute top-2 right-2 z-10 flex items-center gap-1.5 bg-destructive text-destructive-foreground text-xs font-semibold px-2 py-1"
                  data-testid={`voice-recording-indicator-${capture.id}`}
                >
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  {t("editDialog.voiceRecordingIndicator")}
                </div>
              )}
              {isText && isMobile && (
                // On mobile the small inline voice button (still used on
                // desktop) sits in a row that's easy to miss/mis-tap one-handed.
                // A big fixed circular button pinned to the bottom-right of the
                // edit area is reachable with a thumb without hunting for it.
                <Button
                  type="button"
                  data-testid={`capture-edit-voice-fab-${capture.id}`}
                  variant={recording ? "destructive" : "default"}
                  className="absolute bottom-4 right-4 z-20 h-14 w-14 rounded-full p-0 shadow-lg"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={recording ? () => stopRecording() : startRecording}
                >
                  {recording ? <MicrophoneSlash size={24} weight="bold" className="animate-pulse" /> : <Microphone size={24} />}
                </Button>
              )}
            </div>
          </div>
          {organizePreview && (
            <div className="border border-primary/40 bg-secondary p-3 space-y-2" data-testid={`ai-organize-preview-${capture.id}`}>
              <Label className="text-xs flex items-center gap-1.5 text-primary"><Sparkle size={14} /> {t("editDialog.aiOrganizePreviewTitle")}</Label>
              <div className="rich-text-content text-sm bg-card border border-border p-3" dangerouslySetInnerHTML={{ __html: organizePreview }} />
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={applyOrganizePreview} data-testid={`ai-organize-apply-button-${capture.id}`} className="rounded-none gap-2">
                  <Check size={14} weight="bold" /> {t("editDialog.aiApply")}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setOrganizePreview(null)} data-testid={`ai-organize-discard-button-${capture.id}`} className="rounded-none gap-2">
                  <X size={14} weight="bold" /> {t("editDialog.aiDiscard")}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : isPdf ? (
        <div className="space-y-3">
          <div className="flex justify-end items-center gap-1">
            {hasQuickActions && renderQuickActions()}
            <Button
              type="button"
              variant="outline"
              size="sm"
              title={t("editDialog.zoomView")}
              data-testid={`capture-edit-zoom-button-${capture.id}`}
              onClick={() => setZoomOpen(true)}
              className="rounded-none h-7 w-7 p-0"
            >
              <CornersOut size={13} />
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              title={t("editDialog.deleteScreenshot")}
              data-testid={`capture-edit-delete-button-${capture.id}`}
              onClick={() => onDelete(capture.id)}
              className="rounded-none h-7 w-7 p-0"
            >
              <Trash size={13} />
            </Button>
          </div>
          <PdfPreview pdfDataUrl={fullPdfSrc} captureId={capture.id} height={MEDIA_PREVIEW_HEIGHT} />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end items-center gap-1">
            {hasQuickActions && renderQuickActions()}
            <Button
              type="button"
              variant="outline"
              size="sm"
              title={t("editDialog.annotate")}
              data-testid={`capture-edit-annotate-button-${capture.id}`}
              onClick={handleAnnotate}
              className="rounded-none h-7 w-7 p-0"
            >
              <PencilSimple size={13} />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              title={t("editDialog.zoomView")}
              data-testid={`capture-edit-zoom-button-${capture.id}`}
              onClick={() => setZoomOpen(true)}
              className="rounded-none h-7 w-7 p-0"
            >
              <CornersOut size={13} />
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              title={t("editDialog.deleteScreenshot")}
              data-testid={`capture-edit-delete-button-${capture.id}`}
              onClick={() => onDelete(capture.id)}
              className="rounded-none h-7 w-7 p-0"
            >
              <Trash size={13} />
            </Button>
          </div>
          <div className="flex items-center justify-center border border-border bg-muted overflow-hidden" style={{ height: MEDIA_PREVIEW_HEIGHT }}>
            <img src={fullImageSrc || capture.image_data} alt={title} className="max-h-full max-w-full object-contain" data-testid={`capture-edit-image-preview-${capture.id}`} />
          </div>
        </div>
      )}
      {!isText && (
        <div className="space-y-2">
          <Label htmlFor={`edit-comment-${capture.id}`}>{t("editDialog.commentLabel")}</Label>
          <Textarea
            id={`edit-comment-${capture.id}`}
            data-testid={`capture-edit-comment-textarea-${capture.id}`}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              commentRef.current = e.target.value;
            }}
            className="rounded-none"
          />
        </div>
      )}
      {aiSuggestion && (
        <div className="border border-primary/40 bg-secondary p-3 space-y-1 relative" data-testid={`ai-suggestion-display-${capture.id}`}>
          <button type="button" onClick={dismissAiSuggestion} data-testid={`ai-suggestion-dismiss-button-${capture.id}`} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
          <Label className="text-xs flex items-center gap-1.5 text-primary"><Lightbulb size={14} /> {t("editDialog.aiSuggestionTitle")}</Label>
          <div className="rich-text-content text-sm pr-4" dangerouslySetInnerHTML={{ __html: aiSuggestion }} />
        </div>
      )}
      {capture.reply && (
        <div className="border border-primary/40 bg-secondary p-3 space-y-1" data-testid={`capture-edit-reply-display-${capture.id}`}>
          <Label className="text-xs flex items-center gap-1.5 text-primary"><ChatCircleText size={14} /> {t("editDialog.replyLabel")}</Label>
          <p className="text-sm whitespace-pre-wrap">{capture.reply}</p>
        </div>
      )}
      {!isText && (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent
            className="max-w-none w-auto max-h-none h-[92vh] p-2 gap-0 bg-background border-border flex items-center justify-center overflow-hidden"
            data-testid={`capture-zoom-dialog-${capture.id}`}
          >
            {isPdf ? (
              <PdfPreview pdfDataUrl={fullPdfSrc} captureId={`zoom-${capture.id}`} height={Math.floor(window.innerHeight * 0.85)} />
            ) : (
              <img
                src={fullImageSrc || capture.image_data}
                alt={title}
                className="h-full w-auto object-contain"
                data-testid={`capture-zoom-image-${capture.id}`}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});
